// SystemSummaryCard — Phase 2 of the Site Overview redesign (djin-cloud#67).
//
// Full-width card showing 8 live site-level power metrics. Data source is the
// `site_summary` payload on the `telemetry:batch` WebSocket event, already
// surfaced via `ProjectSummary`'s memoized `kpiValues`. No new data plumbing.
//
// Design contract: docs/plans/2026-04-20-site-overview-redesign-design.md §4.1.
// Mockup: docs/plans/2026-04-20-site-overview-mockup.html (row 1).
//
// Null-safety: any null KPI renders as "—". No fabricated fallback data.
//
// Power Factor: as of Phase 3 (djin-cloud#67), PF is server-delivered via
// `site_summary.site_power_factor`. The card reads the value directly; no
// client-side cos(atan2(...)) compute.

export interface SystemSummaryCardProps {
  kpiValues: Record<string, number | null>;
}

function formatNumber(val: number | null, fractionDigits = 1): string {
  if (val == null) return '—';
  return val.toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
}

function formatPowerFactor(val: number | null): string {
  if (val == null) return '—';
  return val.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

interface TileProps {
  label: string;
  value: string;
  unit?: string;
  valueClassName?: string;
}

function MetricTile({ label, value, unit, valueClassName }: TileProps) {
  return (
    <div className="rounded-lg p-3 bg-ems-bg-card/40 border border-ems-border">
      <div className="text-[10px] text-ems-text-muted uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-mono font-bold tabular-nums ${valueClassName ?? 'text-ems-text-primary'}`}>
          {value}
        </span>
        {unit && <span className="text-xs text-ems-text-muted">{unit}</span>}
      </div>
    </div>
  );
}

export function SystemSummaryCard({ kpiValues }: SystemSummaryCardProps) {
  const activePower = kpiValues.total_load_kw ?? null;
  const reactive = kpiValues.total_reactive_power_kvar ?? null;
  const apparent = kpiValues.total_apparent_power_kva ?? null;
  const capacityTotal = kpiValues.capacity_total_kw ?? null;
  const capacityOnline = kpiValues.capacity_online_kw ?? null;
  const utilization = kpiValues.site_utilization_pct ?? null;
  const gridFreq = kpiValues.grid_frequency_hz ?? null;
  const powerFactor = kpiValues.site_power_factor ?? null;

  return (
    <section className="card rounded-xl border border-ems-border-bright/50 overflow-hidden bg-ems-bg-card/60">
      <div className="px-5 py-3 flex items-center gap-2 border-b border-ems-border">
        <h3 className="text-sm font-semibold text-ems-text-primary tracking-wide uppercase">
          Live Power Summary
        </h3>
        <div className="ml-auto flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full bg-ems-green animate-ems-pulse"
            aria-hidden="true"
          />
          <span className="text-[10px] text-ems-text-muted uppercase tracking-wider">Live</span>
        </div>
      </div>
      <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <MetricTile
          label="Active Power"
          value={formatNumber(activePower)}
          unit="kW"
          valueClassName="text-ems-green"
        />
        <MetricTile label="Reactive" value={formatNumber(reactive)} unit="kVAR" />
        <MetricTile label="Apparent" value={formatNumber(apparent)} unit="kVA" />
        <MetricTile
          label="Power Factor"
          value={formatPowerFactor(powerFactor)}
          valueClassName="text-ems-cyan"
        />
        <MetricTile label="Gen Capacity" value={formatNumber(capacityTotal)} unit="kW" />
        <MetricTile
          label="Online Capacity"
          value={formatNumber(capacityOnline)}
          unit="kW"
          valueClassName="text-ems-blue"
        />
        <MetricTile
          label="% Load"
          value={formatNumber(utilization)}
          unit="%"
          valueClassName="text-ems-amber"
        />
        <MetricTile
          label="Grid Freq"
          value={formatNumber(gridFreq, 2)}
          unit="Hz"
          valueClassName="text-ems-green"
        />
      </div>
    </section>
  );
}

export default SystemSummaryCard;
